/**
 * Test Data Generator view (doc 147 §6.6.6).
 *
 * Pick any entity, choose count + seed + optional-field policy + output
 * format, and copy/download a Playwright-ready fixture. The generator
 * itself is the pure `testDataGen` module — this view is just a control
 * surface with no business logic.
 */
import { useMemo, useState } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import {
  generateSamples,
  serializeJson,
  serializeTs,
  MAX_SAMPLES,
} from '../state/testDataGen.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { navigate } from '../state/hashRouter.js';
import { COPY_FEEDBACK_MS } from '../state/timings.js';

export type OutputFormat = 'json' | 'ts';
export type IncludeOptional = 'always' | 'sometimes' | 'never';

export interface TestDataGeneratorViewProps {
  manifest: AtlasManifest;
  /** Optional preselected entity (driven by `#testdata/<entityId>` route). */
  entityId?: string;
  onCopy?: (text: string) => Promise<void>;
}

const COUNT_PRESETS = [1, 5, 25] as const;

export function TestDataGeneratorView({
  manifest,
  entityId,
  onCopy,
}: TestDataGeneratorViewProps): React.JSX.Element {
  const entities = useMemo(
    () => [...manifest.entities].sort((a, b) => a.id.localeCompare(b.id)),
    [manifest.entities],
  );
  const initial = entityId ?? entities[0]?.id ?? '';
  const [selectedId, setSelectedId] = useState(initial);
  const [count, setCount] = useState<number>(5);
  const [seed, setSeed] = useState<number>(42);
  const [format, setFormat] = useState<OutputFormat>('json');
  const [includeOptional, setIncludeOptional] = useState<IncludeOptional>('sometimes');
  const [copiedAt, setCopiedAt] = useState<number | null>(null);

  const entity = entities.find((e) => e.id === selectedId) ?? null;

  // PR Q4 — per-field include policy. Defaults to the global setting until
  // the user overrides on a specific field. Reset on entity change.
  type FieldPolicy = 'always' | 'sometimes' | 'omit';
  const [fieldPolicies, setFieldPolicies] = useState<Record<string, FieldPolicy>>({});
  const policyFor = (name: string): FieldPolicy =>
    fieldPolicies[name] ?? (includeOptional === 'never' ? 'omit' : (includeOptional as FieldPolicy));
  const setPolicy = (name: string, p: FieldPolicy): void =>
    setFieldPolicies({ ...fieldPolicies, [name]: p });

  const samples = useMemo(() => {
    if (!entity) return [] as ReturnType<typeof generateSamples>;
    // Step 1 — produce raw samples using the global policy as the baseline.
    const raw = generateSamples(entity, manifest, { seed, count, includeOptional });
    // Step 2 — drop fields whose per-field policy is "omit".
    const omitted = Object.entries(fieldPolicies).filter(([, v]) => v === 'omit').map(([k]) => k);
    if (omitted.length === 0) return raw;
    return raw.map((s) => {
      const out = { ...s };
      for (const k of omitted) delete out[k];
      return out;
    });
  }, [entity, manifest, seed, count, includeOptional, fieldPolicies]);

  const output = useMemo(() => {
    if (!entity) return '';
    return format === 'json' ? serializeJson(samples) : serializeTs(samples, entity.id);
  }, [entity, samples, format]);

  const filename = entity
    ? `${entity.id.toLowerCase()}-fixtures.${format === 'json' ? 'json' : 'ts'}`
    : 'fixtures.json';

  /* c8 ignore start — clipboard / Blob URL — not exercised in jsdom unit tests. */
  const copy = async (): Promise<void> => {
    if (onCopy) {
      await onCopy(output);
    } else if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(output);
    }
    setCopiedAt(Date.now());
  };

  const download = (): void => {
    const blob = new Blob([output], {
      type: format === 'json' ? 'application/json' : 'text/typescript',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };
  /* c8 ignore stop */

  if (entities.length === 0) {
    return (
      <div className="test-data-generator">
        <h2>Test Data Generator</h2>
        <EmptyState
          icon="science"
          title="No entities in the manifest"
          detail="The generator needs at least one entity to sample."
        />
      </div>
    );
  }

  return (
    <div className="test-data-generator">
      <h2>Test Data Generator</h2>
      <p className="subtitle">
        Generate Playwright fixtures + emulator seed data from any entity's schema.
        Output is seeded — the same (entity, seed) always renders identical samples.
      </p>

      <div className="tdg-controls">
        <label className="tdg-field">
          <span>Entity</span>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            aria-label="Entity"
          >
            {entities.map((e) => (
              <option key={e.id} value={e.id}>
                {e.id} · {e.domain}
              </option>
            ))}
          </select>
        </label>

        <label className="tdg-field">
          <span>Count</span>
          <div className="tdg-count">
            {COUNT_PRESETS.map((n) => (
              <button
                key={n}
                type="button"
                className={`btn btn-ghost${count === n ? ' is-active' : ''}`}
                onClick={() => setCount(n)}
                aria-pressed={count === n}
                aria-label={`Generate ${n}`}
              >
                {n}
              </button>
            ))}
            <input
              type="number"
              min={0}
              max={MAX_SAMPLES}
              value={count}
              onChange={(e) =>
                setCount(Math.max(0, Math.min(MAX_SAMPLES, Number(e.target.value) || 0)))
              }
              aria-label="Custom count"
            />
          </div>
        </label>

        <label className="tdg-field">
          <span>Seed</span>
          <input
            type="number"
            value={seed}
            onChange={(e) => setSeed(Number(e.target.value) || 0)}
            aria-label="Seed"
          />
        </label>

        <label className="tdg-field">
          <span>Format</span>
          <div className="tdg-format" role="radiogroup" aria-label="Output format">
            <button
              type="button"
              className={`btn btn-ghost${format === 'json' ? ' is-active' : ''}`}
              onClick={() => setFormat('json')}
              role="radio"
              aria-checked={format === 'json'}
            >
              JSON
            </button>
            <button
              type="button"
              className={`btn btn-ghost${format === 'ts' ? ' is-active' : ''}`}
              onClick={() => setFormat('ts')}
              role="radio"
              aria-checked={format === 'ts'}
            >
              TypeScript
            </button>
          </div>
        </label>

        <label className="tdg-field">
          <span>Optional fields</span>
          <select
            value={includeOptional}
            onChange={(e) => setIncludeOptional(e.target.value as IncludeOptional)}
            aria-label="Optional fields"
          >
            <option value="sometimes">Sometimes</option>
            <option value="always">Always</option>
            <option value="never">Never</option>
          </select>
        </label>
      </div>

      <div className="tdg-actions">
        <button type="button" className="btn btn-primary" onClick={() => void copy()}>
          {copiedAt && Date.now() - copiedAt < COPY_FEEDBACK_MS ? 'Copied ✓' : 'Copy'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={download}>
          Download {filename}
        </button>
        {entity && (
          <button
            type="button"
            className="btn btn-ghost tdg-pipe"
            onClick={() => navigate({ kind: 'sandbox', seedEntity: entity.id })}
            title="Open sandbox seeded with this entity"
          >
            <Icon name="science" size="sm" />
            Pipe to sandbox
          </button>
        )}
        <span className="tdg-meta">
          {entity ? `${count} × ${entity.id}` : ''}
        </span>
      </div>

      {entity && samples.length > 0 && (
        <section className="tdg-preview" aria-label="Preview grid">
          <header className="tdg-preview-head">
            <span className="tdg-preview-label">
              PREVIEW · {Math.min(samples.length, 3)} of {samples.length} rows
            </span>
          </header>
          <div className="tdg-preview-scroll">
            <table className="tdg-preview-table">
              <thead>
                <tr>
                  {entity.fields.map((f) => (
                    <th key={f.name} scope="col" className="mono">{f.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {samples.slice(0, 3).map((s, i) => (
                  <tr key={i}>
                    {entity.fields.map((f) => (
                      <td key={f.name} className="mono">
                        {formatCell((s as Record<string, unknown>)[f.name])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {entity && (
        <section className="tdg-policies" aria-label="Per-field include policy">
          <header className="tdg-policies-head">
            <span className="tdg-policies-label">PER-FIELD INCLUDE POLICY</span>
            <span className="tdg-policies-hint">
              Override the global "Optional fields" setting per field.
            </span>
          </header>
          <ul className="tdg-policies-list">
            {entity.fields.map((f) => {
              const p = policyFor(f.name);
              return (
                <li key={f.name} className="tdg-policy-row">
                  <span className="mono tdg-policy-name">{f.name}</span>
                  <span className="mono tdg-policy-type">{f.type}{f.optional ? '?' : ''}</span>
                  <div className="tdg-policy-toggle" role="radiogroup" aria-label={`Policy for ${f.name}`}>
                    {(['always', 'sometimes', 'omit'] as const).map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        className={`tdg-policy-btn${p === opt ? ' is-active' : ''}`}
                        aria-checked={p === opt}
                        role="radio"
                        onClick={() => setPolicy(f.name, opt)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <pre className="tdg-output" aria-label="Generated fixture">
        <code>{output}</code>
      </pre>
    </div>
  );
}

function formatCell(v: unknown): string {
  if (v === undefined) return '—';
  if (v === null) return 'null';
  if (typeof v === 'string') return v.length > 24 ? v.slice(0, 23) + '…' : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return `[${v.length}]`;
  return '{…}';
}
